import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import H7 from '@base/H7/H7';
import H6 from '@base/H6/H6';
import ChevronRight from '@icon/ChevronRight';
import EducatorCredential from '@comp/EducatorCredential/EducatorCredential';
import dateHelper from '@utils/dates';
import textHelper from '@utils/textHelper';
import styleHelper from '@utils/styleHelper';
import Dynamic from '@base/Dynamic/Dynamic';
import { segment } from '@actions/analytics';
import { breakpoints, Spacings } from '@styles/index';
import { EDUCATOR_ABOUT } from '@constants/segment';
import { emptyObject } from '@constants/empty';
import useEducatorDetailsSegmentData from '@hooks/useEducatorDetailsSegmentData';
import useProfileStats from '@api/hooks/profile/useProfileStats';
import useProfile from '@api/hooks/profile/useProfile';

const EducatorHighlightsDrawer = Dynamic(() =>
  import('./EducatorHighlightsDrawer')
);

const Wrapper = styled.div`
  grid-column: 19/25;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: 32px;
    grid-column: span 10;
  }
`;

const Container = styled.div`
  overflow-y: auto;
  position: sticky;
  top: 184px;
  width: ${styleHelper.getWidthFromGridColumn(6)};
  transition: all 100ms;
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_3B} ${Spacings.SPACING_0B}
    ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    position: relative;
    top: 0;
    overflow: hidden;
    padding: 0;
    width: 100%;
  }
`;

const Stats = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const ViewMore = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_2B};
  align-items: center;
  cursor: pointer;
`;

const Title = styled(H6)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
`;

const EducatorHighlights = ({ username, isPlatinumEducator }) => {
  const { profileData: profileInfo } = useProfile(username) || emptyObject;
  const { profileStats = emptyObject } = useProfileStats(username);
  const segmentData = useEducatorDetailsSegmentData({
    educatorProfile: profileInfo
  });

  const [showDetailedHighlights, setShowDetailedHighlights] = useState(false);

  const onClickViewMore = () => {
    setShowDetailedHighlights(true);
    segment.track(EDUCATOR_ABOUT, { ...segmentData });
  };

  const onCloseHighlights = () => setShowDetailedHighlights(false);

  useEffect(() => {
    setShowDetailedHighlights(false);
  }, [username]);

  const liveMinutes = profileStats.live_minutes_30_days || 0;

  const {
    credentials,
    languages,
    becomeEducatorAt,
    isEducator,
    isExEducator,
    firstName,
    knowsAbout,
    dateJoined
  } = profileInfo;
  if (!Array.isArray(credentials)) return null;
  if (!isEducator || isExEducator) return null;

  const showViewMore = credentials?.find(
    (credential) => credential.value?.length > 1
  );
  const workedAt = credentials?.find((item) => item.type === 1)?.value?.[0];
  const studiedAt = credentials?.find((item) => item.type === 2)?.value?.[0];
  const accomplishment = credentials?.find((item) => item.type === 4)
    ?.value?.[0];
  const location = credentials?.find((item) => item.type === 3)?.value?.[0];

  const becomeEducatorAtString = becomeEducatorAt
    ? `${dateHelper.getDateMonthString(
        becomeEducatorAt,
        true
      )}, ${dateHelper.getYear(becomeEducatorAt)}`
    : null;

  const languagesCount = languages?.length;
  const languageString =
    languages &&
    (languagesCount > 1
      ? `${languages?.slice(0, languagesCount - 1).join(', ')} and ${
          languages[languagesCount - 1]
        }`
      : languages?.[0]);

  return (
    <Wrapper $isPlatinumEducator={isPlatinumEducator}>
      <Container $isPlatinumEducator={isPlatinumEducator}>
        <Title color="var(--color-text-primary)">Educator highlights</Title>
        <Stats>
          {workedAt && (
            <EducatorCredential
              icon="StarCircleOutline"
              baseText={workedAt.currently_related ? 'Works at' : 'Worked at'}
              highlightedText={workedAt.title}
            />
          )}
          {studiedAt && (
            <EducatorCredential
              icon="Dedications"
              baseText={
                studiedAt.currently_related ? 'Studies at' : 'Studied at'
              }
              highlightedText={studiedAt.title}
            />
          )}
          {accomplishment && (
            <EducatorCredential
              icon="StarCircleOutline"
              baseText={accomplishment.title}
            />
          )}
          {location && (
            <EducatorCredential
              icon="Location"
              baseText="Lives in"
              highlightedText={location.title}
            />
          )}
          {becomeEducatorAtString && (
            <EducatorCredential
              icon="Credit"
              baseText="Unacademy Educator since"
              highlightedText={becomeEducatorAtString}
            />
          )}
          {liveMinutes > 0 && (
            <EducatorCredential
              icon="Live"
              atStart
              baseText="taught in last 30 days"
              highlightedText={`${textHelper.commas(
                Math.round(liveMinutes / 60)
              )} live minutes`}
            />
          )}
          {languageString && (
            <EducatorCredential
              icon="Language"
              baseText="Knows"
              highlightedText={languageString}
            />
          )}
        </Stats>
        {showViewMore && (
          <ViewMore onClick={onClickViewMore}>
            <H7 color="var(--color-text-primary)">View more</H7>
            <ChevronRight size="16px" />
          </ViewMore>
        )}
      </Container>
      {showDetailedHighlights && (
        <EducatorHighlightsDrawer
          showDetailedHighlights={showDetailedHighlights}
          onCloseHighlights={onCloseHighlights}
          firstName={firstName}
          credentials={credentials}
          languages={languages}
          dateOfJoining={dateJoined}
          knowsAbout={knowsAbout}
        />
      )}
    </Wrapper>
  );
};

export default EducatorHighlights;

EducatorHighlights.propTypes = {
  username: PropTypes.string.isRequired
};
